import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight } from "lucide-react";
import { GiChiliPepper } from "react-icons/gi";
import defaultImageUrl from "@/assets/default-menu-item.webp";
import { useMenuItem } from "@/query/useMenuItem";

export const MenuItemViewer2: React.FC = () => {
  const { t } = useTranslation();
  const { restaurantId, itemId } = useParams();

  const { data: menuItem } = useMenuItem(itemId!);
  const navigate = useNavigate();

    const formattedPrice = useMemo(() => {
      if (!menuItem) return "";
      return new Intl.NumberFormat("he-IL", {
        style: "currency",
        currency: "ILS",
      }).format(menuItem.price);
    }, [menuItem]);

  if (!menuItem) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="animate-spin" size={24} />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-white text-gray-800 max-w-4xl mx-auto">
      {/* header Image Section */}
      <div className="relative w-full h-64 sm:h-96 overflow-hidden border-b-8 border-[#7fb438]">
        <img
          src={menuItem.imageUrl ?? defaultImageUrl}
          alt={menuItem.name}
          className="w-full h-full object-cover object-center"
        />
        <Button
          variant="ghost"
          onClick={() => navigate(`/menu/${restaurantId}`)}
          className="absolute top-4 right-4 bg-white/80 hover:bg-white rounded-full p-2 shadow-md"
          aria-label="Back"
        >
          <ArrowRight
            style={{ height: "2rem", width: "2rem" }}
            className="text-gray-900"
          />
        </Button>
      </div>

  <section className="max-w-4xl mx-2 px-4 sm:px-6 md:px-8 py-4 space-y-6 bg-[#7fb438] text-white mt-3 rounded-lg">
        <div className="flex flex-col gap-3">
          <div className="flex flex-row justify-between gap-4">
            <h1 className="text-4xl font-bold tracking-tight sm:order-1">
              {menuItem.name}
            </h1>
            {/* Spicy level: show 3 fire icons, filled according to level */}
            <div className="flex items-center gap-1 sm:order-2" aria-hidden>
              {(() => {
                const level = Number(menuItem.spicyLevel ?? 0);                
                if (level === 0) {
                  return null;
                }
                return [0, 1, 2].map((i) => (
                  <GiChiliPepper
                    key={i}
                    className={
                      i < level
                        ? "text-red-500 h-6 w-6"
                        : "text-gray-300 h-6 w-6"
                    }
                    aria-hidden
                  />
                ));
              })()}
              <span className="sr-only">{menuItem.spicyLevel || "none"}</span>
            </div>
          </div>
          <p className="text-base leading-relaxed">
            {menuItem.description}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {!menuItem.isAvailable && (
            <Badge variant="destructive">{t("menuItem.unavailable")}</Badge>
          )}

          {menuItem.calories && (
            <Badge variant="outline">{menuItem.calories} kcal</Badge>
          )}

          {menuItem.preparationTime && (
            <Badge variant="outline">{menuItem.preparationTime} min</Badge>
          )}

          {menuItem.allergens && (
            <Badge variant="outline">Allergens: {menuItem.allergens}</Badge>
          )}
        </div>   

        <div className="bg-white text-[#7fb438] w-full flex justify-start text-left p-4 rounded-lg">
          <span className="text-black text-lg">{t('ThePrice')}:</span>{" "}
          <span className="text-[#7fb438] font-bold text-2xl ml-3">{formattedPrice}</span>
        </div>
      </section>
    </main>
  );
};

export default MenuItemViewer2;
